import { useState, useEffect, useRef, useMemo } from "react";
import "./App.css";
import { Toaster, toast } from 'sonner';
import {
  Scissors, Calendar, MapPin, Phone, Star, Menu, X, Check, Clock, Award, Share, PlusSquare,
  ChevronRight, MessageCircle, ChevronDown, Instagram, Send, Bell, Info, Users, Sparkles,
  Scale, ShieldCheck, Cookie, User, Loader2, Trophy, Gift, Download, HelpCircle,
  Banknote, CreditCard, Smartphone
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { ScrollArea } from './components/ui/scroll-area';
import AdminDashboard from './components/AdminDashboard';
import { useTenantConfig } from './hooks/useTenantConfig';

// ============================================================================
// 🎨 CONFIGURACIÓN DINÁMICA DEL CLIENTE
// ============================================================================
// Configuración por defecto (fallback si falla la carga dinámica)
const DEFAULT_CONFIG = {
  branding: {
    nombre: "DEMO",
    nombreCompleto: "Barbería Demo",
    colores: { primario: "#f59e0b", secundario: "#27272a", acento: "#f59e0b" },
    fuente: "Oswald"
  },
  contacto: {
    direccion: "Dirección no configurada",
    whatsapp: null,
    telegram: null,
    instagram: null,
    coords: { lat: 43.3209, lng: -3.0186, zoom: 15 }
  },
  servicios: [
    { id: 'adulto', nombre: "Corte Adulto", precio: 12.00, precioHabitual: 10.00, duracion: 30, descripcion: "Estilo personalizado" }
  ],
  fidelidad: {
    activo: true,
    visitasParaPremio: 10,
    descuentoHabitual: 2.00,
    textoDescuento: "¡Cliente habitual! Descuento aplicado.",
    textoPremio: "¡ESTE CORTE ES GRATIS!"
  },
  galeria: ["https://images.unsplash.com/photo-1599011176306-4a96f1516d4d?q=85&w=800"],
  faqs: [{ pregunta: "¿Pregunta ejemplo?", respuesta: "Respuesta ejemplo" }],
  api: { endpoint: "", username: "demo" },
  reservas: { anticipacionMaxima: 30, intervaloSlots: 30, horasCancelacion: 24, permiteCancelar: true },
  legal: { textoFooter: "© 2026 Barbería Demo" }
};

// 💰 HELPERS PARA DATOS DERIVADOS
const getServices = (config) => config.servicios.map(s => ({
  id: s.id,
  name: s.nombre,
  price: s.precio,
  duration: s.duracion,
  description: s.descripcion,
  precioHabitual: s.precioHabitual
}));

const getGallery = (config) => config.galeria;

const getFaqs = (config) => config.faqs.map(f => ({
  question: f.pregunta,
  answer: f.respuesta
}));

const getLegalData = (config) => ({
  aviso: {
    title: "Aviso Legal",
    content: `En cumplimiento del artículo 10 de la Ley 34 / 2002, de 11 de julio, de Servicios de la Sociedad de la Información y Comercio Electrónico(LSSICE), se exponen los siguientes datos identificativos:

Responsable: ${config.branding.nombreCompleto}.
  Ubicación: ${config.contacto.direccion}.
    Actividad: Servicios de peluquería y estética masculina.

El acceso y uso de este sitio web atribuye la condición de USUARIO, que acepta los términos aquí expuestos. El sitio web de ${config.branding.nombreCompleto} proporciona acceso a información sobre servicios y reserva de citas online.`
  },
  privacidad: {
    title: "Política de Privacidad (RGPD)",
    content: `De conformidad con el Reglamento(UE) 2016 / 679(RGPD) y la LOPDGDD 3 / 2018, le informamos:

Responsable: ${config.branding.nombreCompleto}.
  Finalidad: Gestión de reservas de citas, programa de fidelización y comunicaciones de recordatorio vía Telegram o WhatsApp.
    Legitimación: Ejecución de un contrato de servicio y consentimiento del interesado.
      Conservación: Los datos se conservarán mientras exista un interés mutuo para mantener el fin del tratamiento.
        Derechos: Tiene derecho a acceder, rectificar y suprimir los datos, así como otros derechos contactando con nosotros presencialmente o vía web.`
  },
  cookies: {
    title: "Política de Cookies",
    content: `Este sitio web utiliza cookies técnicas y de personalización:

1. Cookies Técnicas: Necesarias para el sistema de login de "Mis Citas" y la gestión del carrito de servicios.
2. Cookies de Preferencia: Almacenan su decisión sobre el consentimiento de cookies.

Usted puede restringir, bloquear o borrar las cookies de este sitio utilizando su navegador.Si desactiva las cookies técnicas, el sistema de reservas podría no funcionar correctamente.`
  },
  terminos: {
    title: "Términos y Condiciones",
    content: `Condiciones de Servicio de ${config.branding.nombreCompleto}:

1. Reservas: La confirmación de la cita implica la aceptación del horario seleccionado.
2. Cancelaciones: Se permite la cancelación gratuita a través de la web hasta ${config.reservas.horasCancelacion} horas antes de la cita. 
3. Puntualidad: Se ruega puntualidad. Pasados 10 minutos de la hora acordada, la barbería se reserva el derecho de reagendar la cita para no perjudicar a otros clientes.
4. Fidelidad: El sistema de puntos(${config.fidelidad.visitasParaPremio} visitas = 1 gratis) es personal e intransferible.`
  }
});

const TUTORIAL_STEPS = [
  { title: "1. Abre el menú", desc: "Toca el botón 'Compartir' (cuadrado con flecha) abajo en tu iPhone.", icon: <Share className="w-8 h-8 text-amber-500" /> },
  { title: "2. Busca la opción", desc: "Desliza y pulsa en 'Añadir a pantalla de inicio'.", icon: <PlusSquare className="w-8 h-8 text-amber-500" /> },
  { title: "3. Confirma", desc: "Dale a 'Añadir' arriba a la derecha.", icon: <Check className="w-8 h-8 text-green-500" /> }
];

const getUserId = (username) => {
  const storageKey = `${username}_user_id`;
  let userId = localStorage.getItem(storageKey);
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem(storageKey, userId);
  }
  return userId;
};

const parseBackendDate = (dateStr) => {
  if (!dateStr) return new Date();
  const [datePart, timePart] = dateStr.split(' ');
  const [day, month, year] = datePart.split('/');
  return new Date(`${year}-${month}-${day}T${timePart}:00`);
};

const isValidPhone = (phoneStr) => {
  if (!phoneStr) return false;
  let clean = phoneStr.replace(/\D/g, '');
  if (clean.length > 9 && clean.startsWith('34')) clean = clean.substring(2);
  if (clean.length !== 9) return false;
  if (new Set(clean).size === 1) return false;
  return !(['123456789', '987654321', '000000000'].includes(clean));
};

const RevealOnScroll = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(20px)", transition: `opacity 0.6s ease - out ${delay} s, transform 0.6s ease - out ${delay} s` }}>
      {children}
    </div>
  );
};

// --- MODAL LEGAL DINÁMICO ---
const LegalModalInternal = ({ type, onClose, config }) => {
  if (!type) return null;
  const legalData = getLegalData(config);
  const data = legalData[type];
  return (
    <div className="fixed inset-0 z-[40000] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-zinc-900 rounded-2xl border border-zinc-800 flex flex-col max-h-[85vh] shadow-2xl animate-in zoom-in duration-300">
        <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-950">
          <h3 className="font-bold text-amber-500 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> {data.title}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <ScrollArea className="flex-1 p-6">
          <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{data.content}</p>
          <Button onClick={onClose} className="w-full mt-8 bg-zinc-800 border border-zinc-700">CERRAR</Button>
        </ScrollArea>
      </div>
    </div>
  );
};

// --- COMPONENTE COOKIES MEJORADO ---
const CookieBannerInternal = ({ username }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const consent = localStorage.getItem(`${username}_cookie_consent`);
    if (!consent) setShow(true);
  }, []);

  const handleConsent = (status) => {
    localStorage.setItem(`${username}_cookie_consent`, status);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[30000] p-4 animate-in slide-in-from-bottom-full duration-700">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-zinc-900 border border-amber-500/20 rounded-2xl p-6 shadow-2xl flex flex-col md:flex-row items-center gap-6 backdrop-blur-xl">
          <div className="bg-amber-500/10 p-4 rounded-full">
            <Cookie className="w-10 h-10 text-amber-500" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h4 className="text-white font-bold text-lg mb-1">Aviso de Privacidad</h4>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Utilizamos cookies propias para mejorar tu experiencia de reserva y gestionar tus puntos de fidelidad. ¿Aceptas el uso de cookies técnicas?
            </p>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button onClick={() => handleConsent('declined')} className="flex-1 md:flex-none px-6 text-sm font-bold text-zinc-500 hover:text-white transition-colors">RECHAZAR</button>
            <Button onClick={() => handleConsent('accepted')} className="flex-1 md:flex-none bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 h-12">ACEPTAR</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MODAL INSTALACIÓN ---
const InstallModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-[20000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl flex flex-col max-h-[85vh]">
        <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-950 shrink-0">
          <h3 className="font-bold text-white">Instalar App</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {TUTORIAL_STEPS.map((step, idx) => (
              <div key={idx} className="flex gap-4 items-start p-3 bg-zinc-950/50 rounded-lg border border-zinc-800/50">
                <div className="mt-1 shrink-0">{step.icon}</div>
                <div>
                  <h4 className="font-bold text-sm text-amber-500">{step.title}</h4>
                  <p className="text-xs text-zinc-400 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
            <Button onClick={onClose} className="w-full bg-amber-500 text-black font-bold mt-2">ENTENDIDO</Button>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

// --- MODAL MIS CITAS ---
const MyAppointmentsModal = ({ onClose, apiEndpoint, username }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) { toast.error("Completa todos los campos"); return; }
    if (!isValidPhone(formData.phone)) { toast.error("Teléfono inválido. Debe tener 9 dígitos."); return; }
    setLoading(true);
    try {
      const resAppt = await fetch(`${apiEndpoint}/api/my-appointments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone, username: username })
      });
      const dataAppt = await resAppt.json();
      const resStats = await fetch(`${apiEndpoint}/api/client-status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone })
      });
      const dataStats = await resStats.json();
      if (dataAppt.status === 'ok') {
        setAppointments(dataAppt.appointments || []);
        const progress = dataStats.cycle_progress !== undefined ? dataStats.cycle_progress : (dataStats.past_visits % 10 || 10);
        setStats({ ...dataStats, displayProgress: progress });
        setStep(2);
      } else { toast.error(dataAppt.message || "Error al buscar citas"); }
    } catch { toast.error("Error de conexión"); } finally { setLoading(false); }
  };

  const handleCancel = async (eventId, dateStr) => {
    const appointmentDate = parseBackendDate(dateStr);
    const now = new Date();
    const diffHours = (appointmentDate - now) / (1000 * 60 * 60);

    if (diffHours < 24) { toast.error("Para cancelar con menos de 24h, llama al local.", { duration: 4000 }); return; }
    if (!window.confirm("¿Seguro que quieres cancelar esta cita?")) return;

    try {
      const res = await fetch(`${apiEndpoint}/api/cancel-appointment`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event_id: eventId, phone: formData.phone })
      });
      if (res.ok) {
        toast.success("Cita cancelada");
        const resAppt = await fetch(`${apiEndpoint}/api/my-appointments`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: formData.phone, username: username })
        });
        const data = await resAppt.json();
        setAppointments(data.appointments || []);
      } else { toast.error("No se pudo cancelar"); }
    } catch (e) { toast.error("Error de conexión"); }
  };

  return (
    <div className="fixed inset-0 z-[20000] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
        <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-950 shrink-0">
          <h3 className="font-bold text-white text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-amber-500" /> Zona Cliente
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <ScrollArea className="flex-1 p-4">
          {step === 1 ? (
            <form onSubmit={handleLogin} className="space-y-6 pt-4">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-zinc-800 rounded-full mx-auto flex items-center justify-center mb-4 border border-zinc-700">
                  <User className="w-8 h-8 text-zinc-500" />
                </div>
                <h2 className="text-xl font-bold text-white">Bienvenido de nuevo</h2>
                <p className="text-zinc-400 text-sm">Introduce tus datos para ver tus puntos y citas</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Tu nombre (Usuario)</Label>
                  <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="bg-zinc-950 border-zinc-800 h-12 text-lg" placeholder="Ej: César" />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="bg-zinc-950 border-zinc-800 h-12 text-lg" placeholder="644123456" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold h-12 text-lg shadow-[0_0_20px_rgba(245,158,11,0.2)]">
                {loading ? <Loader2 className="animate-spin" /> : "ENTRAR A MI ZONA"}
              </Button>
            </form>
          ) : (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
              {/* FORZAR VISUALIZACIÓN: Usamos IIFE para calcular stats con fallback */}
              {(() => {
                const s = stats || { past_visits: 0, displayProgress: 0, cycle_progress: 0 };
                // Lógica de progreso (Duplicada de BookingSection para consistencia)
                const currentVisits = s.past_visits;
                const displayProgress = s.displayProgress !== undefined ? s.displayProgress : (currentVisits % 10 === 0 && currentVisits > 0 ? 10 : currentVisits % 10);
                const isRewardReached = displayProgress === 10;

                return (
                  <div className={`bg-gradient-to-br from-zinc-900 to-zinc-950 border rounded-2xl p-5 shadow-xl relative overflow-hidden transition-all duration-500 ${isRewardReached ? "border-green-500 shadow-green-500/10" : "border-amber-500/30"}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-black font-bold text-xl shadow-lg border-2 border-white/10 ${isRewardReached ? "bg-green-500" : "bg-amber-500"}`}>
                        {formData.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-zinc-400 text-xs uppercase tracking-wider">Miembro Habitual</p>
                        <h3 className="text-white font-bold text-lg">Hola, {formData.name}</h3>
                      </div>
                      <div className="ml-auto bg-amber-500/10 p-2 rounded-full">
                        <Trophy className={`w-6 h-6 ${isRewardReached ? "text-green-500" : "text-amber-500"}`} />
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-end">
                        <span className="text-zinc-400 text-sm">Tu progreso</span>
                        <div className="flex items-baseline gap-2">
                          {isRewardReached ? (
                            <>
                              <span className="text-zinc-500 line-through text-lg font-bold">10/10</span>
                              <span className="text-green-500 text-2xl font-black animate-pulse drop-shadow-[0_0_10px_rgba(34,197,94,0.8)]">¡GRATIS! 🎁</span>
                            </>
                          ) : (
                            <span className="text-xl font-bold text-amber-500">{displayProgress}/10</span>
                          )}
                        </div>
                      </div>

                      {/* BARRA DE PROGRESO */}
                      <div className="h-6 bg-zinc-800 rounded-full overflow-hidden border border-zinc-700 relative shadow-inner">
                        <div
                          className={`h-full transition-all duration-1000 ease-out flex items-center justify-center ${isRewardReached ? "bg-gradient-to-r from-green-500 to-green-400 shadow-[0_0_20px_#22c55e]" : "bg-gradient-to-r from-amber-600 to-amber-500"}`}
                          style={{ width: `${isRewardReached ? 100 : displayProgress * 10}%` }}
                        >
                          {isRewardReached && <div className="absolute inset-0 bg-white/30 animate-pulse"></div>}
                        </div>
                      </div>

                      {/* TICKET DE CORTE GRATIS (VISUAL) */}
                      {isRewardReached && (
                        <div className="mt-6 mx-auto max-w-sm bg-gradient-to-br from-zinc-900 to-black border-2 border-dashed border-green-500 rounded-xl p-4 shadow-[0_0_30px_rgba(34,197,94,0.2)] transform hover:scale-105 transition-transform cursor-default relative overflow-hidden group">
                          <div className="absolute top-0 right-0 bg-green-500 text-black text-xs font-bold px-2 py-1 rounded-bl-lg">VÁLIDO HOY</div>
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-green-500/10 rounded-full border border-green-500/50">
                              <Gift className="w-8 h-8 text-green-500 animate-bounce" />
                            </div>
                            <div>
                              <h4 className="text-green-400 font-black text-xl tracking-tighter uppercase">Golden Ticket</h4>
                              <p className="text-zinc-400 text-sm">Corte de Pelo 100% Bonificado</p>
                            </div>
                          </div>
                          <div className="mt-3 flex justify-between items-center border-t border-zinc-800 pt-2">
                            <span className="text-zinc-500 text-xs font-mono">{formData.phone}</span>
                            <span className="text-green-500 font-bold text-lg">0.00€</span>
                          </div>
                          <div className="absolute -inset-full top-0 block h-full w-1/2 -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-10 group-hover:animate-shine" />
                        </div>
                      )}
                    </div>

                    <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${isRewardReached ? "bg-green-500/10 border-green-500/50" : formData.name ? "bg-zinc-800/50 border-amber-500/30" : "bg-zinc-800/50 border-zinc-700/50"}`}>
                      <Gift className={`w-5 h-5 ${isRewardReached ? "text-green-500" : "text-amber-500"}`} />
                      <p className="text-sm">
                        {isRewardReached ? (
                          <span className="text-green-400 font-bold">¡ENHORABUENA! Tu próximo corte es GRATIS.</span>
                        ) : currentVisits === 0 ? (
                          <span className="text-amber-400 font-semibold">¡Hola! En tu <strong className="text-white">2ª visita</strong> tendrás precio de habitual (10€).</span>
                        ) : (
                          <span className="text-zinc-300">Llega a 10 visitas para un corte <strong className="text-white">GRATIS</strong>.</span>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-3">
                <h4 className="text-zinc-400 text-sm font-medium uppercase tracking-wider">Tus Reservas</h4>
                {appointments.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-zinc-800 rounded-xl bg-zinc-900/50">
                    <Calendar className="w-12 h-12 mx-auto text-zinc-700 mb-2" />
                    <p className="text-zinc-500 text-sm">No tienes citas pendientes</p>
                  </div>
                ) : (
                  appointments.map((apt, idx) => (
                    <div key={idx} className="p-4 rounded-xl border bg-zinc-950 border-zinc-800 group hover:border-zinc-700 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-amber-500/10 shrink-0">
                            <Calendar className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <h4 className="font-bold text-white text-sm">{apt.summary}</h4>
                            <p className="text-amber-500 font-medium mt-1">{apt.start_formatted}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {apt.val_base && apt.val_final && apt.val_final < apt.val_base ? (
                                <>
                                  <span className="text-sm font-bold text-red-500 line-through">{apt.val_base}€</span>
                                  <span className="text-sm font-bold text-green-500">{apt.val_final}€</span>
                                </>
                              ) : apt.val_final ? (
                                <span className="text-sm font-bold text-zinc-300">{apt.val_final}€</span>
                              ) : (
                                <span className="text-xs text-zinc-500">Servicio Reservado</span>
                              )}
                            </div>
                          </div>
                        </div>
                        {(() => {
                          // Asegurar que parseBackendDate existe o usar new Date 
                          // Asumimos parseBackendDate está disponible globalmente en App.js
                          const d = parseBackendDate(apt.start_formatted);
                          const diff = (d - new Date()) / 36e5;
                          return diff > 24 ? (
                            <button onClick={() => handleCancel(apt.id, apt.start_formatted)} className="p-2 text-zinc-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all" title="Cancelar"><X className="w-5 h-5" /></button>
                          ) : <span className="text-[10px] text-orange-500/70 bg-orange-500/10 px-2 py-1 rounded">No cancelable</span>
                        })()}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <Button onClick={() => { setStep(1); setAppointments([]); setFormData({ name: '', phone: '' }); setStats(null); }} variant="outline" className="w-full mt-2 border-zinc-700 hover:bg-zinc-800">Cerrar Sesión</Button>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

// --- MODAL CONFIRMACIÓN ---
const ConfirmationModal = ({ appointmentDetails, onClose, telegramBot }) => {
  const handleTelegramActivation = () => {
    // BOT NAME must be correct in CONFIG (pepe.json)
    const botName = telegramBot || "citas_barbearia_bot";
    const telegramLink = `https://t.me/${botName}?start=web`;
    window.open(telegramLink, '_blank');
  };

  return (
    <div className="fixed inset-0 z-[20000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl">
        <div className="p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">¡Cita Confirmada!</h3>
          <div className="bg-zinc-950 rounded-xl p-4 my-4 text-left">
            <div className="flex items-start gap-3 mb-3">
              <Calendar className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-zinc-400">Fecha y hora</p>
                <p className="text-white font-semibold">{appointmentDetails.datetime}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Scissors className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-zinc-400">Servicios</p>
                <p className="text-white font-semibold">{appointmentDetails.services}</p>
              </div>
            </div>
          </div>
          <button onClick={handleTelegramActivation} className="w-full bg-[#0088cc] hover:bg-[#0077b3] text-white font-bold py-4 px-6 rounded-xl mb-3 flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl">
            <MessageCircle className="w-5 h-5" />
            🔔 Activar Recordatorios por Telegram
          </button>
          <Button onClick={onClose} variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800">Cerrar</Button>
        </div>
      </div>
    </div>
  );
};

const Header = ({ onOpenInstall, onOpenAppointments, title }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-lg border-b border-zinc-800">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#inicio" className="flex items-center gap-2">
            <Scissors className="w-6 h-6 md:w-8 md:h-8 text-amber-500" />
            <span className="text-xl md:text-2xl font-bold font-[var(--font-brand)]">{title}</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#inicio" className="text-sm hover:text-amber-500 font-medium tracking-wide">INICIO</a>
            <a href="#servicios" className="text-sm hover:text-amber-500 font-medium tracking-wide">SERVICIOS</a>
            <button onClick={onOpenAppointments} className="text-sm hover:text-amber-500 font-medium tracking-wide">MIS CITAS</button>
          </nav>
          <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}><Menu className="w-6 h-6" /></button>
        </div>
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-zinc-800 flex flex-col gap-2 bg-zinc-950">
            <a href="#inicio" className="block py-3 px-4 text-sm hover:text-amber-500" onClick={() => setIsMenuOpen(false)}>INICIO</a>
            <button onClick={() => { setIsMenuOpen(false); onOpenAppointments(); }} className="block py-3 px-4 text-sm hover:text-amber-500 text-left">MIS CITAS</button>
            <div className="px-4 pt-2">
              <Button onClick={() => { setIsMenuOpen(false); onOpenInstall(); }} className="w-full bg-zinc-800 text-amber-500 border border-zinc-700">
                <Download className="w-4 h-4 mr-2" /> INSTALAR APP
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

const HeroSection = ({ onCTA, subtitle }) => (
  <section id="inicio" className="relative h-screen flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1520338661084-680395057c93?q=85&w=1920&auto=format&fit=crop')` }} />
    <div className="absolute inset-0 bg-black/60" />
    <RevealOnScroll>
      <div className="relative z-10 container mx-auto px-4 text-center pb-32">
        <p className="text-amber-500 text-sm uppercase mb-4 tracking-[0.3em] font-medium">{subtitle}</p>
        <h1 className="text-5xl md:text-7xl font-bold uppercase mb-6 font-[var(--font-brand)] leading-tight">
          TU ESTILO,<br /><span className="text-amber-500">TU SELLO</span>
        </h1>
        <Button onClick={onCTA} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-6 text-lg transition-transform hover:scale-105">
          RESERVAR AHORA <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </RevealOnScroll>
  </section>
);

const ServicesSection = ({ cart, onToggleService, services }) => (
  <section id="servicios" className="py-20 bg-zinc-950">
    <div className="container mx-auto px-4">
      <RevealOnScroll>
        <h2 className="text-center text-4xl font-bold mb-12 font-[var(--font-brand)]">SERVICIOS</h2>
      </RevealOnScroll>
      <div className="max-w-2xl mx-auto space-y-4">
        {services.map((service, index) => (
          <RevealOnScroll key={service.id} delay={index * 0.1}>
            <RevealOnScroll key={service.id} delay={index * 0.1}>
              <div onClick={() => onToggleService(service)} className={`group relative overflow-hidden p-6 rounded-3xl cursor-pointer transition-all duration-300 ${cart.some(s => s.id === service.id) ? 'bg-zinc-800 ring-2 ring-amber-500 shadow-[0_8px_30px_rgba(245,158,11,0.15)]' : 'bg-zinc-900/50 hover:bg-zinc-900 hover:shadow-xl'} `}>
                <div className="flex items-center gap-5">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${cart.some(s => s.id === service.id) ? 'bg-amber-500 text-black rotate-12' : 'bg-zinc-800 text-zinc-400 group-hover:bg-zinc-700'} `}>
                    {cart.some(s => s.id === service.id) ? <Check className="w-6 h-6" /> : <Scissors className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className={`font-bold text-lg ${cart.some(s => s.id === service.id) ? 'text-white' : 'text-zinc-200'} `}>{service.name}</h3>
                      <p className={`font-bold text-lg ${cart.some(s => s.id === service.id) ? 'text-amber-500' : 'text-zinc-400 group-hover:text-white transition-colors'} `}>{service.price.toFixed(2)} €</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-zinc-500">{service.description}</p>
                      <span className="text-xs font-medium text-zinc-600 bg-zinc-950/50 px-2 py-1 rounded-md ml-2 whitespace-nowrap">{service.duration} min</span>
                    </div>
                  </div>
                </div>
              </div>
            </RevealOnScroll>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </section>
);

// --- NUEVA SECCIÓN: ZONA CLIENTE (MIS CITAS) ---
const ClientAreaSection = ({ onOpen }) => (
  <section className="py-12 bg-zinc-900 border-y border-zinc-800">
    <div className="container mx-auto px-4 text-center">
      <RevealOnScroll>
        <div className="max-w-xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-4 font-[var(--font-brand)]">¿YA TIENES CITA?</h3>
          <p className="text-zinc-400 mb-6">Consulta tus reservas, mira tus puntos de fidelidad o cancela si no puedes venir.</p>
          <Button
            onClick={onOpen}
            className="w-full sm:w-auto bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-white font-bold px-8 py-6 text-lg rounded-xl flex items-center justify-center gap-3 transition-all hover:border-amber-500"
          >
            <User className="w-5 h-5 text-amber-500" />
            VER MIS CITAS
          </Button>
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

const LocationSection = ({ contact }) => (
  <section id="ubicacion" className="py-20 bg-zinc-900">
    <div className="container mx-auto px-4 text-center">
      <RevealOnScroll>
        <h2 className="text-4xl font-bold mb-12 font-[var(--font-brand)]">VISÍTANOS</h2>
      </RevealOnScroll>
      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        <RevealOnScroll>
          <div className="space-y-8 text-left p-6 bg-zinc-950 rounded-2xl border border-zinc-800">
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-zinc-900 rounded-lg text-amber-500"><MapPin /></div>
              <div><h3 className="font-bold text-lg mb-1">Dirección</h3><p className="text-zinc-400">{contact.direccion}</p></div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-zinc-900 rounded-lg text-amber-500"><Clock /></div>
              <div><h3 className="font-bold text-lg mb-1">Horario</h3><p className="text-zinc-400">Lunes - Sábado: 10:00 - 21:00</p><p className="text-zinc-500 text-sm">Descanso: 15:00 - 16:00</p></div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-zinc-900 rounded-lg text-amber-500"><Phone /></div>
              <div>
                <h3 className="font-bold text-lg mb-1">Contacto</h3>
                <p className="text-zinc-400 mb-3">Reserva tu cita online</p>
                <div className="flex gap-3 mt-2">
                  {contact.whatsapp && (
                    <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green-700 text-green-500 hover:bg-green-900/20 transition-colors text-sm">
                      <Phone className="w-4 h-4" /> WhatsApp
                    </a>
                  )}
                  {contact.telegram && (
                    <a href={`https://t.me/${contact.telegram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-700 text-blue-500 hover:bg-blue-900/20 transition-colors text-sm">
                      <MessageCircle className="w-4 h-4" /> Telegram
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </RevealOnScroll>
        <RevealOnScroll delay={0.2}>
          <div className="h-80 md:h-full rounded-3xl overflow-hidden bg-zinc-900 shadow-2xl relative">
            <div className="absolute inset-0 pointer-events-none z-10 rounded-3xl ring-1 ring-inset ring-white/10"></div>
            <iframe
              src="https://maps.google.com/maps?q=43.3240939,-3.0281623&t=&z=18&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
              loading="lazy"
              title="mapa"
            />
          </div>
        </RevealOnScroll>
      </div>
    </div>
  </section>
);

const GallerySection = ({ images }) => (
  <section className="py-20 bg-zinc-950">
    <div className="container mx-auto px-4">
      <RevealOnScroll>
        <div className="text-center mb-12">
          <p className="text-amber-500 text-sm tracking-[0.2em] uppercase mb-2">Nuestro Trabajo</p>
          <h2 className="text-4xl font-bold font-[var(--font-brand)]">GALERÍA</h2>
        </div>
      </RevealOnScroll>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 max-w-5xl mx-auto">
        {images.map((img, index) => (
          <RevealOnScroll key={index} delay={index * 0.1}>
            <div className="aspect-square rounded-xl overflow-hidden relative group cursor-pointer">
              <img src={img} alt={`Trabajo ${index + 1} `} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Instagram className="w-8 h-8 text-white" />
              </div>
            </div>
          </RevealOnScroll>
        ))}
      </div>
      <RevealOnScroll delay={0.3}>
        <div className="text-center mt-10">
          <Button variant="outline" className="rounded-full border-zinc-700 hover:border-amber-500 hover:text-amber-500 px-8 py-6" onClick={() => window.open('https://instagram.com', '_blank')}>
            <Instagram className="w-5 h-5 mr-2" /> Síguenos en Instagram
          </Button>
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

const FAQSection = ({ faqs }) => (
  <section className="py-20 bg-zinc-900">
    <div className="container mx-auto px-4">
      <RevealOnScroll>
        <h2 className="text-center text-3xl font-bold mb-12 font-[var(--font-brand)]">PREGUNTAS FRECUENTES</h2>
      </RevealOnScroll>
      <div className="max-w-2xl mx-auto space-y-4">
        {faqs.map((faq, index) => (
          <RevealOnScroll key={index} delay={index * 0.1}>
            <Accordion type="single" collapsible>
              <AccordionItem value={`faq - ${index} `} className="border border-zinc-800 rounded-xl bg-zinc-950/50 px-4">
                <AccordionTrigger className="hover:no-underline py-5 text-white hover:text-amber-500 transition-colors">
                  <div className="flex items-center gap-3 text-left">
                    <HelpCircle className="w-5 h-5 text-amber-500 shrink-0" />
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 pl-8 pb-4">{faq.answer}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </section>
);

const ChatWidget = ({ isBookingOpen, apiEndpoint }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: '¡Hola! Soy Vicky. ¿En qué puedo ayudarte?' }]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  useEffect(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), [messages, isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;
    const txt = inputValue.trim();
    setInputValue('');
    setMessages(p => [...p, { role: 'user', content: txt }]);
    setIsLoading(true);
    try {
      const res = await fetch(`${apiEndpoint}/api/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: txt, user_id: getUserId() })
      });
      const data = await res.json();
      setMessages(p => [...p, { role: 'assistant', content: data.reply || "No te he entendido bien." }]);
    } catch {
      setMessages(p => [...p, { role: 'assistant', content: "Error de conexión." }]);
    } finally { setIsLoading(false); }
  };

  if (isBookingOpen) return null;
  return (
    <>
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-[50] w-14 h-14 rounded-full flex items-center justify-center shadow-lg bg-amber-500 hover:bg-amber-400 transition-all animate-in fade-in zoom-in">
          <MessageCircle className="w-7 h-7 text-black" />
        </button>
      )}
      {isOpen && (
        <div className="fixed inset-0 z-[19000] bg-zinc-900 flex flex-col md:inset-auto md:bottom-24 md:right-6 md:w-[380px] md:h-[550px] md:rounded-2xl md:border md:border-zinc-800 md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
          <div className="flex justify-between items-center p-4 border-b border-zinc-800 bg-zinc-950 shrink-0">
            <div className="flex gap-3 items-center">
              <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center"><Scissors className="w-4 h-4 text-black" /></div>
              <div><h4 className="font-bold text-sm text-white">Vicky</h4><p className="text-[10px] text-green-500">Online</p></div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-full text-white"><X className="w-4 h-4" /></button>
          </div>
          <ScrollArea className="flex-1 p-4 bg-zinc-900/95">
            <div className="space-y-4 pb-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} `}>
                  <div className={`max - w - [85 %] p - 3 rounded - 2xl text - sm leading - relaxed shadow - sm ${m.role === 'user' ? 'bg-amber-500 text-black rounded-br-none font-medium' : 'bg-zinc-800 text-zinc-100 rounded-bl-none'} `}>
                    {m.content}
                  </div>
                </div>
              ))}
              {isLoading && <div className="text-zinc-500 text-xs animate-pulse pl-2">Vicky está escribiendo...</div>}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
          <form onSubmit={sendMessage} className="p-3 border-t border-zinc-800 bg-zinc-950 flex gap-2 shrink-0 sticky bottom-0">
            <Input value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Escribe aquí..." className="bg-zinc-900 border-zinc-800 text-white focus:border-amber-500" disabled={isLoading} style={{ fontSize: '16px' }} />
            <Button type="submit" size="icon" className="bg-amber-500 text-black hover:bg-amber-400"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
};

// --- BOOKING FORM (CON MAGIA DE PRECIOS DINÁMICOS) ---

// --- BOOKING FORM (CON MAGIA DE PRECIOS DINÁMICOS) ---
const BookingForm = ({ cart, totalPrice, totalDuration, onClose, onSuccess, config }) => {
  // Config derivada
  const apiEndpoint = config.api.endpoint;
  const username = config.api.username;
  const fidelidadConfig = config.fidelidad;

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', date: '', time: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isHabitual, setIsHabitual] = useState(false);
  const [isNextFree, setIsNextFree] = useState(false);

  // EFECTO: DETECTAR CLIENTE HABITUAL Y APLICAR DESCUENTO O PREMIO
  useEffect(() => {
    if (formData.phone.length === 9 && isValidPhone(formData.phone)) {
      const checkStatus = async () => {
        try {
          const res = await fetch(`${apiEndpoint}/api/client-status`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phone: formData.phone })
          });
          const data = await res.json();

          // Lógica de premio de fidelidad (visita 10)
          // Usamos cycle_progress del backend. Si es 10, es premio.
          const isPrize = (data.cycle_progress === 10) || (data.past_visits > 0 && data.past_visits % 10 === 0);

          if (isPrize) {
            setIsNextFree(true);
            toast.success("¡ESTE CORTE ES GRATIS!", { icon: '🎁', duration: 6000 });
          } else if (data.is_habitual) {
            setIsHabitual(true);
            toast.success("¡Cliente habitual! Descuento aplicado.", { icon: '👋' });
          } else {
            setIsHabitual(false);
            setIsNextFree(false);
          }
        } catch (e) { console.error(e); }
      };
      const timeoutId = setTimeout(checkStatus, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setIsHabitual(false);
      setIsNextFree(false);
    }
  }, [formData.phone, apiEndpoint]);

  // CÁLCULO DEL PRECIO FINAL (DINÁMICO)
  const calculateFinalPrice = () => {
    if (isNextFree) return 0; // Si le toca premio, 0€
    return cart.reduce((total, item) => {
      // Aplicar descuento habitual solo si está configurado
      if (item.id === 'adulto' && isHabitual && fidelidadConfig.activo) {
        return total + (item.price - fidelidadConfig.descuentoHabitual);
      }
      return total + item.price;
    }, 0);
  };

  const finalPrice = calculateFinalPrice();

  useEffect(() => {
    if (formData.date) {
      setLoadingSlots(true);
      setFormData(prev => ({ ...prev, time: '' }));
      fetch(`${apiEndpoint}/api/slots?date=${formData.date}`)
        .then(res => res.json())
        .then(data => {
          if (data.status === 'ok') setAvailableSlots(data.slots || []);
          else { toast.error("Error al cargar horarios"); setAvailableSlots([]); }
        })
        .catch(() => { toast.error("Error de conexión"); setAvailableSlots([]); })
        .finally(() => setLoadingSlots(false));
    } else {
      setAvailableSlots([]);
    }
  }, [formData.date, apiEndpoint]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.date || !formData.time) { toast.error("Rellena todos los campos"); return; }
    if (!isValidPhone(formData.phone)) { toast.error("Teléfono inválido. Debe tener 9 dígitos."); return; }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${apiEndpoint}/api/appointment`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData, date: `${formData.date} ${formData.time}`, user_id: getUserId(username),
          services: cart.map(s => s.name).join(', '), duration: totalDuration,
          total_price: finalPrice
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'ok') {
        setConfirmedAppointment({
          datetime: `${formData.date.split('-').reverse().join('/')} a las ${formData.time} `,
          services: cart.map(s => s.name).join(', ')
        });
        setShowConfirmation(true);
      } else { toast.error(data.message || "Error al reservar."); }
    } catch { toast.error("Error de conexión."); } finally { setIsSubmitting(false); }
  };

  if (showConfirmation && confirmedAppointment) {
    return <ConfirmationModal appointmentDetails={confirmedAppointment} onClose={() => { setShowConfirmation(false); onSuccess(); }} telegramBot={config.contacto.telegram} />;
  }

  return (
    <div className="fixed inset-0 z-[20000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-950 shrink-0">
          <div>
            <h3 className="text-xl font-bold font-[var(--font-brand)]">AGENDAR CITA</h3>
            <p className="text-sm text-zinc-500">
              Total:
              {isNextFree ? (
                <span className="text-green-500 font-bold ml-2">0.00 € (¡PREMIO!)</span>
              ) : isHabitual && cart.some(s => s.id === 'adulto') ? (
                <>
                  <span className="line-through text-red-500 mx-1">{totalPrice.toFixed(2)}€</span>
                  <span className="text-green-500 font-bold">{finalPrice.toFixed(2)}€</span>
                </>
              ) : (
                <span> {finalPrice.toFixed(2)}€ </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <ScrollArea className="flex-1 p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nombre</Label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="bg-zinc-950 border-zinc-800" placeholder="Tu nombre" /></div>

            <div>
              <Label>Teléfono (9 dígitos)</Label>
              <Input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="bg-zinc-950 border-zinc-800" placeholder="644123456" />
              {isNextFree && <p className="text-xs text-green-500 animate-pulse mt-1 font-bold">¡Visita 10 alcanzada! Próximo corte GRATIS.</p>}
              {!isNextFree && isHabitual && <p className="text-xs text-green-500 animate-pulse mt-1">¡Por ser habitual se te queda en 10€!</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Fecha</Label><Input type="date" min={new Date().toISOString().split('T')[0]} value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} className="bg-zinc-950 border-zinc-800" /></div>
              <div>
                <Label>Hora {loadingSlots && <span className="text-xs text-amber-500">(...)</span>}</Label>
                <div className="relative">
                  <select value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} className="w-full h-10 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-sm appearance-none" disabled={loadingSlots || !formData.date}>
                    <option value="">{loadingSlots ? "Cargando..." : (formData.date ? "Seleccionar" : "Elige fecha")}</option>
                    {availableSlots.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 rotate-90 pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="pt-2">
              <Button type="submit" disabled={isSubmitting || loadingSlots || !availableSlots.length} className={`w-full font-bold h-12 transition-all ${isNextFree ? "bg-green-500 hover:bg-green-400 text-black shadow-green-500/20" : "bg-amber-500 hover:bg-amber-400 text-black shadow-amber-500/20"} `}>
                {isSubmitting ? <Loader2 className="animate-spin mx-auto" /> : `CONFIRMAR RESERVA (${finalPrice.toFixed(2)}€)`}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </div>
    </div>
  );
};

// --- CLIENT APP COMPONENT ---
function ClientApp({ config }) {
  // Config derivada
  const services = useMemo(() => getServices(config), [config]);
  const gallery = useMemo(() => getGallery(config), [config]);
  const faqs = useMemo(() => getFaqs(config), [config]);
  const legalData = useMemo(() => getLegalData(config), [config]);

  const apiEndpoint = config.api.endpoint;
  const username = config.api.username;
  const branding = config.branding;

  const [showInstall, setShowInstall] = useState(false);
  const [showAppointments, setShowAppointments] = useState(false);

  const [cart, setCart] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(true);

  const [legalType, setLegalType] = useState(null);

  const total = cart.reduce((s, i) => s + i.price, 0);
  const duration = cart.reduce((s, i) => s + i.duration, 0);
  const toggle = (s) => setCart(p => p.find(i => i.id === s.id) ? p.filter(i => i.id !== s.id) : [...p, s]);

  // --- COMPONENTE INTERNO: MODAL LEGAL ---
  const LegalModalInternal = ({ type, onClose }) => {
    if (!type) return null;
    const data = legalData[type];
    if (!data) return null;
    return (
      <div className="fixed inset-0 z-[40000] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-zinc-900 rounded-2xl border border-zinc-800 flex flex-col max-h-[85vh] shadow-2xl animate-in zoom-in duration-300">
          <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-950">
            <h3 className="font-bold text-amber-500 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> {data.title}
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded-full"><X className="w-5 h-5" /></button>
          </div>
          <ScrollArea className="flex-1 p-6">
            <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{data.content}</p>
            <Button onClick={onClose} className="w-full mt-8 bg-zinc-800 border border-zinc-700 font-bold">ENTENDIDO</Button>
          </ScrollArea>
        </div>
      </div>
    );
  };

  // --- SECCIÓN: BANNER DE PROMOCIONES ACTIVAS ---
  const PromotionsSection = () => {
    const activePromos = config.promociones?.filter(p => p.activo) || [];
    if (activePromos.length === 0) return null;

    return (
      <section className="py-12 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {activePromos.map((promo, idx) => (
              <RevealOnScroll key={promo.id} delay={idx * 0.1}>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
                    <Sparkles className="w-8 h-8 text-white" />
                    <h3 className="text-2xl md:text-3xl font-black text-white uppercase">
                      {promo.texto}
                    </h3>
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  {promo.codigo && (
                    <div className="inline-block bg-black/30 px-6 py-2 rounded-full">
                      <span className="text-white font-mono font-bold tracking-wider">
                        Código: {promo.codigo}
                      </span>
                    </div>
                  )}
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // --- SECCIÓN: EQUIPO ---
  const TeamSection = ({ team }) => {
    const barberos = team || [];
    if (barberos.length === 0) return null;

    return (
      <section className="py-20 bg-zinc-900">
        <div className="container mx-auto px-4">
          <RevealOnScroll>
            <div className="text-center mb-12">
              <p className="text-amber-500 text-sm tracking-[0.2em] uppercase mb-2">Nuestro Equipo</p>
              <h2 className="text-4xl font-bold font-[var(--font-brand)]">BARBEROS PROFESIONALES</h2>
            </div>
          </RevealOnScroll>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {barberos.filter(b => b.activo).map((barbero, index) => (
              <RevealOnScroll key={barbero.id} delay={index * 0.1}>
                <div className="bg-zinc-950 rounded-2xl overflow-hidden border border-zinc-800 hover:border-amber-500 transition-all duration-300 group">
                  {barbero.foto ? (
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={barbero.foto}
                        alt={barbero.nombre}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-square bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                      <Scissors className="w-20 h-20 text-amber-500/30" />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{barbero.nombre}</h3>
                    <p className="text-amber-500 font-medium text-sm mb-3">{barbero.especialidad}</p>
                    {barbero.descripcion && (
                      <p className="text-zinc-400 text-sm">{barbero.descripcion}</p>
                    )}
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // --- SECCIÓN: MÉTODOS DE PAGO ---
  const PaymentMethodsSection = ({ methods }) => {
    const metodos = methods || {};
    const available = [];
    if (metodos.efectivo) available.push({ name: 'Efectivo', icon: Banknote });
    if (metodos.tarjeta) available.push({ name: 'Tarjeta', icon: CreditCard });
    if (metodos.bizum) available.push({ name: 'Bizum', icon: Smartphone });
    if (metodos.paypal) available.push({ name: 'PayPal', icon: CreditCard }); // Fallback icon

    if (available.length === 0) return null;

    return (
      <section className="py-12 bg-zinc-950 border-y border-zinc-800">
        <div className="container mx-auto px-4">
          <RevealOnScroll>
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-lg font-bold text-white mb-6 font-[var(--font-brand)]">MÉTODOS DE PAGO</h3>
              <div className="flex flex-wrap justify-center gap-4">
                {available.map((metodo, idx) => (
                  <div
                    key={idx}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl px-6 py-4 flex items-center gap-3 hover:border-amber-500 transition-colors group"
                  >
                    <metodo.icon className="w-6 h-6 text-zinc-400 group-hover:text-amber-500 transition-colors" />
                    <span className="text-white font-medium">{metodo.name}</span>
                  </div>
                ))}
              </div>
              {metodos.texto && (
                <p className="text-zinc-500 text-sm mt-4">{metodos.texto}</p>
              )}
            </div>
          </RevealOnScroll>
        </div>
      </section>
    );
  };

  // --- COMPONENTE INTERNO: COOKIES ---
  const CookieBannerInternal = ({ username }) => {
    const [show, setShow] = useState(false);
    useEffect(() => {
      const consent = localStorage.getItem(`${username}_cookie_consent`);
      if (!consent) setShow(true);
    }, [username]);

    const handleConsent = (status) => {
      localStorage.setItem(`${username}_cookie_consent`, status);
      setShow(false);
    };

    if (!show) return null;

    return (
      <div className="fixed bottom-0 left-0 right-0 z-[30000] p-4 animate-in slide-in-from-bottom-full duration-700">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-zinc-900 border border-amber-500/20 rounded-2xl p-6 shadow-2xl flex flex-col md:flex-row items-center gap-6 backdrop-blur-xl">
            <div className="bg-amber-500/10 p-4 rounded-full shrink-0">
              <Cookie className="w-10 h-10 text-amber-500" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h4 className="text-white font-bold text-lg mb-1">Privacidad y Cookies</h4>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Utilizamos cookies técnicas para gestionar tus citas y puntos de fidelidad. ¿Aceptas nuestra <span className="text-amber-500 underline cursor-pointer" onClick={() => setLegalType('cookies')}>política de cookies</span>?
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button onClick={() => handleConsent('declined')} className="flex-1 md:flex-none px-6 text-sm font-bold text-zinc-500 hover:text-white transition-colors">RECHAZAR</button>
              <Button onClick={() => handleConsent('accepted')} className="flex-1 md:flex-none bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 h-12">ACEPTAR</Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // --- COMPONENTE INTERNO: FOOTER ---
  const FooterInternal = ({ config }) => (
    <footer className="py-12 border-t border-zinc-800 bg-zinc-950 px-4">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center gap-2 mb-4">
              <Scissors className="w-8 h-8 text-amber-500" />
              <span className="text-2xl font-bold font-[var(--font-brand)] tracking-wider">{config.branding.nombre}</span>
            </div>
            <p className="text-zinc-500 text-sm max-w-xs text-center md:text-left leading-relaxed">
              {config.contacto.direccion ? `Barbería ubicada en ${config.contacto.direccion}. ` : 'Barbería profesional. '}
              Tu estilo es nuestro sello de identidad.
            </p>
          </div>
          <div className="text-center md:text-left">
            <h4 className="text-white font-bold mb-6 flex items-center justify-center md:justify-start gap-2">
              <div className="w-1 h-4 bg-amber-500 rounded-full" /> MÁS INFORMACIÓN
            </h4>
            <ul className="space-y-4">
              <li className="flex items-center justify-center md:justify-start gap-3 group cursor-pointer" onClick={() => setLegalType('aviso')}>
                <div className="w-1.5 h-1.5 rounded-full border border-zinc-600 group-hover:bg-amber-500 transition-colors" />
                <span className="text-zinc-400 group-hover:text-white text-sm transition-colors">Aviso Legal</span>
              </li>
              <li className="flex items-center justify-center md:justify-start gap-3 group cursor-pointer" onClick={() => setLegalType('privacidad')}>
                <div className="w-1.5 h-1.5 rounded-full border border-zinc-600 group-hover:bg-amber-500 transition-colors" />
                <span className="text-zinc-400 group-hover:text-white text-sm transition-colors">Política de Privacidad</span>
              </li>
              <li className="flex items-center justify-center md:justify-start gap-3 group cursor-pointer" onClick={() => setLegalType('cookies')}>
                <div className="w-1.5 h-1.5 rounded-full border border-zinc-600 group-hover:bg-amber-500 transition-colors" />
                <span className="text-zinc-400 group-hover:text-white text-sm transition-colors">Política de Cookies</span>
              </li>
            </ul>
          </div>
          <div className="text-center md:text-left">
            <h4 className="text-white font-bold mb-6 flex items-center justify-center md:justify-start gap-2">
              <div className="w-1 h-4 bg-amber-500 rounded-full" /> CLIENTES
            </h4>
            <ul className="space-y-4">
              <li className="flex items-center justify-center md:justify-start gap-3 group cursor-pointer" onClick={() => window.open(`https://wa.me/${config.contacto.whatsapp}`, '_blank')}>
                <div className="w-1.5 h-1.5 rounded-full border border-zinc-600 group-hover:bg-amber-500 transition-colors" />
                <span className="text-zinc-400 group-hover:text-white text-sm transition-colors">Contactar con Barbería</span>
              </li>
              <li className="flex items-center justify-center md:justify-start gap-3 group cursor-pointer" onClick={() => setLegalType('terminos')}>
                <div className="w-1.5 h-1.5 rounded-full border border-zinc-600 group-hover:bg-amber-500 transition-colors" />
                <span className="text-zinc-400 group-hover:text-white text-sm transition-colors">Términos y Condiciones</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-zinc-800 text-center">
          <p className="text-xs text-zinc-600">{config.legal.textoFooter || `© 2026 ${branding.nombre}. Todos los derechos reservados.`}</p>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-white pb-20 font-sans selection:bg-amber-500/30">
      <Toaster position="top-center" theme="dark" />
      <Header onOpenInstall={() => setShowInstall(true)} onOpenAppointments={() => setShowAppointments(true)} title={branding.nombre} />
      <HeroSection onCTA={() => document.getElementById('servicios').scrollIntoView({ behavior: 'smooth' })} subtitle={branding.nombreCompleto || branding.nombre} />
      <ServicesSection cart={cart} onToggleService={toggle} services={services} />
      <TeamSection team={config.barberos} />
      <ClientAreaSection onOpen={() => setShowAppointments(true)} />
      <LocationSection contact={config.contacto} />
      <PaymentMethodsSection methods={config.metodosPago} />
      <GallerySection images={gallery} />
      <FAQSection faqs={faqs} />
      <FooterInternal config={config} />
      <CookieBannerInternal username={username} />

      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 pr-24 bg-zinc-950 border-t border-zinc-800 flex justify-between items-center z-40 shadow-2xl animate-in slide-in-from-bottom-5">
          <div><p className="font-bold text-lg text-white">{total.toFixed(2)} €</p><p className="text-xs text-zinc-400">{cart.length} servicios • {duration} min</p></div>
          <Button onClick={() => setShowBooking(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 shadow-[0_0_15px_rgba(245,158,11,0.3)]">CONTINUAR <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      )}

      {showInstall && <InstallModal onClose={() => setShowInstall(false)} />}
      {showBooking && <BookingForm cart={cart} totalPrice={total} totalDuration={duration} onClose={() => setShowBooking(false)} onSuccess={() => { setShowBooking(false); setCart([]); }} config={config} />}
      {showAppointments && <MyAppointmentsModal onClose={() => setShowAppointments(false)} apiEndpoint={apiEndpoint} username={username} />}
      {legalType && <LegalModalInternal type={legalType} onClose={() => setLegalType(null)} config={config} />}
      <ChatWidget isBookingOpen={showBooking} apiEndpoint={apiEndpoint} />
    </div>
  );
}



function App() {
  // 🔄 Cargar configuración dinámica del tenant
  const { config: tenantConfig, loading, error } = useTenantConfig();

  // Usar configuración dinámica si está disponible, sino usar fallback
  const CONFIG = tenantConfig || window.APP_CONFIG || DEFAULT_CONFIG;

  // Mostrar loading mientras carga la configuración
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-white text-lg">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return <AppContent CONFIG={CONFIG} />;
}

// Componente principal con toda la lógica (sin cambios)
function AppContent({ CONFIG }) {
  const [isAdmin, setIsAdmin] = useState(window.location.pathname === '/admin');

  useEffect(() => {
    if (window.location.pathname === '/admin') {
      setIsAdmin(true);
    }
  }, []);

  if (isAdmin) {
    return <AdminDashboard apiEndpoint={CONFIG.api.endpoint} />;
  }

  return <ClientApp config={CONFIG} />;
}

export default App;